import React from 'react';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import Logo from '../../components/devNectar_new_logo.png';
import {
    Users,
    Settings,
    LogOut,
    LayoutDashboard,
    FolderKanban,
    Layers,
    Building2,
    GraduationCap,
    UserCheck,
    ChevronRight,
    Target,
    Receipt,
    FileText,
    CreditCard,
    Shield,
    Calendar,
    BarChart3
} from 'lucide-react';

const AdminLayout: React.FC = () => {
    const navigate = useNavigate();
    const userStr = localStorage.getItem('adminUser');
    const user = userStr ? JSON.parse(userStr) : { username: 'Admin', is_root: true, permissions: [] };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const navItems: { label: string; icon: React.ReactElement; path: string; rootOnly: boolean; pageName?: string }[] = [
        { label: 'Dashboard', icon: <LayoutDashboard />, path: '/admin/dashboard', rootOnly: false },
        { label: 'Leads', icon: <Users />, path: '/admin/leads', rootOnly: false, pageName: 'Leads Management' },
        { label: 'CRM', icon: <Target />, path: '/admin/crm', rootOnly: false, pageName: 'CRM' },
        { label: 'Activities', icon: <Calendar />, path: '/admin/crm/activities', rootOnly: false, pageName: 'Activities' },
        { label: 'Quotes', icon: <Receipt />, path: '/admin/sales/quotes', rootOnly: false, pageName: 'Sales' },
        { label: 'Contracts', icon: <FileText />, path: '/admin/sales/contracts', rootOnly: false, pageName: 'Sales' },
        { label: 'Expenses', icon: <CreditCard />, path: '/admin/expenses', rootOnly: false, pageName: 'Expense Management' },
        { label: 'Reporting', icon: <BarChart3 />, path: '/admin/reporting', rootOnly: false, pageName: 'Reporting' },
        { label: 'Audit Logs', icon: <Shield />, path: '/admin/audit-logs', rootOnly: true, pageName: 'Audit Logs' },
        { label: 'Projects', icon: <FolderKanban />, path: '/admin/projects', rootOnly: false, pageName: 'Project Management' },
        { label: 'Portfolio', icon: <Layers />, path: '/admin/portfolio', rootOnly: false, pageName: 'Portfolio Editor' },
        { label: 'Accounts', icon: <Building2 />, path: '/admin/accounts', rootOnly: false, pageName: 'Account Management' },
        { label: 'Careers', icon: <GraduationCap />, path: '/admin/careers', rootOnly: false, pageName: 'Account Management' },
        { label: 'User Management', icon: <UserCheck />, path: '/admin/users', rootOnly: true },
        { label: 'Settings', icon: <Settings />, path: '/admin/settings', rootOnly: false, pageName: 'Settings' },
    ];

    const filteredNavItems = navItems.filter(item => {
        if (user.is_root) return true;
        if (item.rootOnly) return false;
        if (!item.pageName) return true;
        return user.permissions?.some((p: any) => p.page_name === item.pageName);
    });

    return (
        <div className="flex h-screen w-screen bg-gray-50 overflow-hidden font-sans">
            {/* Sidebar */}
            <aside className="w-64 flex-shrink-0 h-full bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white flex flex-col shadow-xl z-20">
                <div className="p-4 border-b border-blue-800/50 flex-shrink-0">
                    <Link to="/admin/dashboard" className="flex flex-col items-center gap-2 group">
                        <div className="bg-white rounded-xl px-4 py-2 shadow-md flex items-center justify-center w-full transition-transform duration-200 group-hover:scale-[1.02]">
                            <img
                                src={Logo}
                                alt="devNectar Logo"
                                className="h-10 w-auto max-w-full object-contain"
                            />
                        </div>
                        <p className="text-[10px] font-semibold text-blue-300 uppercase tracking-widest text-center">Admin Control Panel</p>
                    </Link>
                </div>

                <nav className="flex-1 overflow-y-auto pt-6 no-scrollbar">
                    <ul className="space-y-2 px-3 pb-6">
                        {filteredNavItems.map((item, index) => (
                            <li key={index}>
                                <Link
                                    to={item.path}
                                    className="flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-white/10 transition-colors group"
                                >
                                    <span className="text-blue-300 group-hover:text-white transition-colors">
                                        {React.cloneElement(item.icon, { size: 20 } as any)}
                                    </span>
                                    <span className="font-medium text-sm">{item.label}</span>
                                    <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
                <header className="h-16 flex-shrink-0 bg-gradient-to-r from-blue-900 via-blue-900 to-indigo-900 border-b border-blue-800/30 flex items-center justify-between px-8 shadow-sm z-10">
                    <div className="flex items-center gap-4">
                        <h3 className="text-lg font-bold text-white tracking-tight capitalize">
                            {window.location.pathname.split('/').pop()?.replace('-', ' ')}
                        </h3>
                        <div className="text-xs text-white/70 font-medium bg-white/10 px-3 py-1 rounded-full border border-white/20 uppercase tracking-widest">
                            Live Environment
                        </div>
                    </div>

                    {/* User Profile & Logout - Moved to Header */}
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-white/10 text-white flex items-center justify-center text-xs font-bold border border-white/20">
                                {user.username.charAt(0).toUpperCase()}
                            </div>
                            <div className="hidden md:block">
                                <p className="text-sm font-semibold text-white">{user.username}</p>
                                <p className="text-xs text-blue-200">{user.is_root ? 'Root Admin' : 'Sub Admin'}</p>
                            </div>
                        </div>
                        <div className="h-8 w-px bg-white/20 mx-2"></div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-red-400 hover:text-red-300 hover:bg-white/5 px-3 py-2 rounded-lg transition-all text-sm font-medium"
                            title="Logout"
                        >
                            <LogOut size={18} />
                            <span className="hidden md:inline">Logout</span>
                        </button>
                    </div>
                </header>

                <section className="flex-1 overflow-y-auto p-8 custom-scrollbar no-scrollbar">
                    <Outlet />
                </section>
            </main>
        </div>
    );
};

export default AdminLayout;
